import React, { MouseEvent } from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';
import { RouteBasePaths, SettingsRoutes } from 'routes';
import { ClinicFormTemplateFragment, FormTemplateType } from 'shared/types/graphql';
import Badge from 'shared/components/Badges/Badge';
import { getClinicWidgetRequestTypeDisplayName } from 'shared/utils/widgetRequests';
import { Flex, UnorderedList, ListItem, Link, Box, Popover } from '@televet/kibble-ui';
import { LinkedForm } from '.';
import { formatPluralization } from 'shared/utils';

interface IFormBadgesProps {
  formTemplate: ClinicFormTemplateFragment;
  linkedAutomations?: LinkedForm[];
}

const FormBadges = ({ formTemplate, linkedAutomations }: IFormBadgesProps): JSX.Element | null => {
  const badges = [];

  if (!formTemplate.isActive) {
    badges.push(<Badge backgroundColor="background.subtle">Archived</Badge>);
  }

  if (formTemplate.formTemplateType === FormTemplateType.FillInTheBlank) {
    badges.push(
      <Popover
        trigger="hover"
        isLazy={true}
        anchor={
          <Box>
            <Badge>Fill In the Blank</Badge>
          </Box>
        }
      >
        {`This form contains 'Fill In the Blank' fields and cannot be used in Automations, Service Reminders, or Website Widget Requests.`}
      </Popover>,
    );
  }

  if (!!linkedAutomations?.length && formTemplate.formTemplateType !== FormTemplateType.ServiceReminder) {
    badges.push(
      <Popover
        trigger="hover"
        isLazy={true}
        anchor={
          <Box>
            <Badge>Automation</Badge>
          </Box>
        }
      >
        <span>
          This form is being used for the following{' '}
          {formatPluralization({ word: 'automation', singularCriteria: linkedAutomations?.length })}:
          <UnorderedList>
            {linkedAutomations?.map((data) => (
              <ListItem bg="background.contrast" key={data.automationId}>
                <Link
                  as={ReactRouterLink}
                  onClick={(e: MouseEvent): void => e.stopPropagation()}
                  to={`${RouteBasePaths.Automations}/${data.automationId}`}
                  variant="interactiveOnContrast"
                >
                  {data.automationName}
                </Link>
              </ListItem>
            ))}
          </UnorderedList>
        </span>
      </Popover>,
    );
  }

  if (formTemplate.formTemplateType === FormTemplateType.ServiceReminder) {
    badges.push(
      <Popover
        trigger="hover"
        isLazy={true}
        anchor={
          <Box>
            <Badge>Service Reminder</Badge>
          </Box>
        }
      >
        <span>
          This form is being used by{' '}
          <Link
            as={ReactRouterLink}
            variant="interactiveOnContrast"
            onClick={(e: MouseEvent): void => e.stopPropagation()}
            to={`${RouteBasePaths.Settings}${SettingsRoutes.ClinicReminders}`}
          >
            Service Reminders
          </Link>
        </span>
      </Popover>,
    );
  }

  // TODO: We should be using the formtemplatetype here to determine if this badge should be shown
  // Currently, there is a bug where the template type is not updated when a form is removed from a service reminder
  if (formTemplate.clinicWidgetRequestTypes?.length) {
    badges.push(
      <Popover
        trigger="hover"
        isLazy={true}
        anchor={
          <Box>
            <Badge>Website Widget</Badge>
          </Box>
        }
      >
        <Box>
          This form is being used for the following Website Widget request{' '}
          {formatPluralization({ word: 'option', singularCriteria: formTemplate.clinicWidgetRequestTypes?.length })}:
          <UnorderedList>
            {formTemplate.clinicWidgetRequestTypes?.map((clinicWidgetRequestType) => {
              return (
                <ListItem key={clinicWidgetRequestType.id}>
                  <Link
                    as={ReactRouterLink}
                    variant="interactiveOnContrast"
                    onClick={(e: MouseEvent): void => e.stopPropagation()}
                    to={{
                      pathname: `${RouteBasePaths.Settings}${SettingsRoutes.ClinicWidget}`,
                      state: { editClinicWidgetRequestType: clinicWidgetRequestType.id },
                    }}
                  >
                    {getClinicWidgetRequestTypeDisplayName(clinicWidgetRequestType)}
                  </Link>
                </ListItem>
              );
            })}
          </UnorderedList>
        </Box>
      </Popover>,
    );
  }

  return badges.length ? (
    <Flex gap={2} mt={3} ml="35px" wrap="wrap">
      {badges.map((badge, index) => (
        <React.Fragment key={index}>{badge}</React.Fragment>
      ))}
    </Flex>
  ) : null;
};

export default FormBadges;
